import { Spinner } from "@ext/enterprise/components/admin/ui-kit/Spinner";
import { OFERTA_LINK } from "@ext/enterprise-cloud/logic/billing/OfertaLink";
import t from "@ext/localization/locale/translate";
import { Button, type ButtonProps, LoadingButtonTemplate } from "@ui-kit/Button";
import { Dialog, DialogBody, DialogContent } from "@ui-kit/Dialog";
import { FormHeader } from "@ui-kit/Form";
import type { ReactNode } from "react";

interface PayModalLayoutProps {
	open: boolean;
	title: string;
	error: string | null;
	onOpenChange: (open: boolean) => void;
	children: ReactNode;
	description?: string;
}

export const PayModalLayout = ({ open, title, error, onOpenChange, children, description }: PayModalLayoutProps) => (
	<Dialog onOpenChange={onOpenChange} open={open}>
		<DialogContent data-modal-root>
			<FormHeader description={description} icon="credit-card" title={title} />
			<DialogBody>
				{error && <div className="mb-3 text-destructive text-sm">{error}</div>}
				<div className="flex flex-col gap-4">{children}</div>
			</DialogBody>
		</DialogContent>
	</Dialog>
);

export const PaymentInfoLoader = () => (
	<div className="flex items-center justify-center w-full">
		<div className="flex flex-col items-center gap-2">
			<Spinner size="medium" />
		</div>
	</div>
);

export const ButtonWithLoadingState = ({
	disabled,
	onClick,
	text,
	loadingText,
	variant,
	isLoading,
}: {
	disabled: boolean;
	onClick: () => void;
	text: string;
	loadingText?: string;
	variant: ButtonProps["variant"];
	isLoading: boolean;
}) => {
	return isLoading ? (
		<LoadingButtonTemplate className="w-full" text={loadingText ?? text} variant={variant} />
	) : (
		<Button className="w-full" disabled={disabled} onClick={onClick} variant={variant}>
			{text}
		</Button>
	);
};

export const OfferAcceptanceNotice = () => (
	<span className="text-muted-foreground text-sm text-center">
		{t("enterprise-cloud.org-settings.subscription.payment.offer-acceptance-prefix")}{" "}
		<a className="underline" href={OFERTA_LINK} rel="noreferrer" target="_blank">
			{t("enterprise-cloud.org-settings.subscription.payment.offer")}
		</a>
	</span>
);

export const PayModalSummary = ({ children }: { children: ReactNode }) => (
	<div className="flex flex-col gap-4 rounded-2xl border p-4">{children}</div>
);

export const PayModalSummarySeparator = () => <div className="h-px bg-border" />;

export type RowAccent = "primary" | "default" | "muted";

interface RowProps {
	label: ReactNode;
	value?: ReactNode;
	accent?: RowAccent;
}

const rowClassNameByAccent: Record<RowAccent, string> = {
	primary: "items-start font-semibold text-lg",
	default: "items-center font-normal text-sm",
	muted: "items-center text-muted-foreground font-normal text-sm",
};

const rowValueClassNameByAccent: Record<RowAccent, string> = {
	primary: "shrink-0 whitespace-nowrap text-right font-semibold text-xl",
	default: "shrink-0 whitespace-nowrap text-right",
	muted: "shrink-0 whitespace-nowrap text-right",
};

export const Row = ({ label, value, accent = "default" }: RowProps) => (
	<div className={`flex justify-between gap-4 ${rowClassNameByAccent[accent]}`}>
		<span>{label}</span>
		{value !== undefined && <span className={rowValueClassNameByAccent[accent]}>{value}</span>}
	</div>
);

export const formatCurrencyWithSymbol = (amount: number | string, currency: string): string =>
	new Intl.NumberFormat(undefined, {
		style: "currency",
		currency,
		currencyDisplay: "symbol",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(Number(amount));

export const getNextPeriodStartDate = (billingPeriod: "month" | "year") => {
	const date = new Date();
	if (billingPeriod === "year") date.setFullYear(date.getFullYear() + 1);
	else date.setMonth(date.getMonth() + 1);
	return date;
};
